import React from "react";
import "./CourseAuthSectionStyles.css";
import {
  FaArrowLeft,
  FaArrowRight,
  FaEnvelope,
  FaKey,
  FaLock,
  FaPaperPlane,
  FaShieldAlt,
  FaSignOutAlt,
  FaUser,
} from "react-icons/fa";

export type CourseAuthMode = "login" | "register" | "forgot";

type CourseAuthSectionProps = {
  authMode: CourseAuthMode;
  loadingTitle?: string;
  displayName: string;
  email: string;
  resetEmail: string;
  password: string;
  rememberLogin: boolean;
  authSubmitting: boolean;
  resetPasswordSending: boolean;
  errorText: string;
  infoText: string;
  onDisplayNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onResetEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onRememberLoginChange: (value: boolean) => void;
  onAuthSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
  onForgotPasswordSubmit: (
    event: React.FormEvent<HTMLFormElement>,
  ) => Promise<void>;
  onBackToCourses: () => void;
  onOpenForgotPassword: () => void;
  onOpenLoginFromForgot: () => void;
  onToggleAuthMode: () => void;
  onLogout: () => Promise<void>;
};

const CourseAuthSection: React.FC<CourseAuthSectionProps> = ({
  authMode,
  loadingTitle,
  displayName,
  email,
  resetEmail,
  password,
  rememberLogin,
  authSubmitting,
  resetPasswordSending,
  errorText,
  infoText,
  onDisplayNameChange,
  onEmailChange,
  onResetEmailChange,
  onPasswordChange,
  onRememberLoginChange,
  onAuthSubmit,
  onForgotPasswordSubmit,
  onBackToCourses,
  onOpenForgotPassword,
  onOpenLoginFromForgot,
  onToggleAuthMode,
  onLogout,
}) => {
  if (loadingTitle) {
    return (
      <aside className="course-login-column">
        <article className="course-auth-card course-auth-loading-card">
          <div className="course-auth-particles" aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
          </div>

          <div className="course-auth-badge">
            <FaShieldAlt />
            <span>Bitte warten</span>
          </div>

          <h1>{loadingTitle}</h1>

          <p>Der Zugang wird sicher geprüft.</p>

          <button
            type="button"
            className="course-auth-safe"
            onClick={onLogout}
          >
            <FaSignOutAlt />
            <span>Abmelden</span>
          </button>
        </article>
      </aside>
    );
  }

  if (authMode === "forgot") {
    return (
      <aside className="course-login-column">
        <article className="course-auth-card course-forgot-card">
          <div className="course-auth-particles" aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
          </div>

          <button
            type="button"
            className="course-auth-back"
            onClick={onBackToCourses}
            aria-label="Zurück zu den Kursen"
          >
            <FaArrowLeft />
          </button>

          <div className="course-auth-badge">
            <FaKey />
            <span>Passwort zurücksetzen</span>
          </div>

          <h1>Passwort vergessen?</h1>

          <p>
            Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen sicheren
            Link, mit dem Sie Ihr Passwort zurücksetzen können.
          </p>

          <form
            className="course-auth-form"
            onSubmit={onForgotPasswordSubmit}
            noValidate
          >
            <label>
              <span>E-Mail-Adresse</span>

              <div className="course-auth-input">
                <FaEnvelope />
                <input
                  type="email"
                  value={resetEmail}
                  placeholder="name@email.com"
                  autoComplete="email"
                  onChange={(event) => onResetEmailChange(event.target.value)}
                  autoFocus
                />
              </div>
            </label>

            {infoText && <div className="course-auth-info">{infoText}</div>}

            {errorText && <div className="course-auth-error">{errorText}</div>}

            <button
              type="submit"
              className="course-auth-submit"
              disabled={resetPasswordSending}
            >
              {resetPasswordSending ? (
                <span>Link wird gesendet...</span>
              ) : (
                <>
                  <span>Link senden</span>
                  <FaPaperPlane />
                </>
              )}
            </button>
          </form>

          <button
            type="button"
            className="course-auth-safe"
            onClick={onOpenLoginFromForgot}
          >
            <FaArrowLeft />
            <span>Zurück zum Einloggen</span>
          </button>
        </article>
      </aside>
    );
  }

  return (
    <aside className="course-login-column">
      <article className="course-auth-card">
        <div className="course-auth-particles" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
        </div>

        <button
          type="button"
          className="course-auth-back"
          onClick={onBackToCourses}
          aria-label="Zurück zu den Kursen"
        >
          <FaArrowLeft />
        </button>

        <div className="course-auth-badge">
          <FaShieldAlt />
          <span>Premium-Zugang</span>
        </div>

        <h1>
          {authMode === "login"
            ? "Einloggen und Kurs öffnen"
            : "Konto erstellen"}
        </h1>

        <p>
          Bitte loggen Sie sich mit Ihrer E-Mail-Adresse ein. Danach prüfen wir
          Ihren Kurszugang automatisch.
        </p>

        <form className="course-auth-form" onSubmit={onAuthSubmit} noValidate>
          {authMode === "register" && (
            <label>
              <span>Name</span>

              <div className="course-auth-input">
                <FaUser />
                <input
                  type="text"
                  value={displayName}
                  placeholder="Ihr Name"
                  autoComplete="name"
                  onChange={(event) => onDisplayNameChange(event.target.value)}
                />
              </div>
            </label>
          )}

          <label>
            <span>E-Mail-Adresse</span>

            <div className="course-auth-input">
              <FaEnvelope />
              <input
                type="email"
                value={email}
                placeholder="name@email.com"
                autoComplete="email"
                onChange={(event) => onEmailChange(event.target.value)}
                autoFocus
              />
            </div>
          </label>

          <label>
            <span>Passwort</span>

            <div className="course-auth-input">
              <FaKey />
              <input
                type="password"
                value={password}
                autoComplete={
                  authMode === "login" ? "current-password" : "new-password"
                }
                onChange={(event) => onPasswordChange(event.target.value)}
              />
            </div>
          </label>

          <div className="course-auth-options">
            <label className="course-auth-remember">
              <input
                type="checkbox"
                checked={rememberLogin}
                onChange={(event) =>
                  onRememberLoginChange(event.target.checked)
                }
              />
              <span className="course-auth-check" />
              <span>Angemeldet bleiben</span>
            </label>

            <button
              type="button"
              className="course-auth-forgot"
              onClick={onOpenForgotPassword}
            >
              Passwort vergessen?
            </button>
          </div>

          {infoText && <div className="course-auth-info">{infoText}</div>}

          {errorText && <div className="course-auth-error">{errorText}</div>}

          <button
            type="submit"
            className="course-auth-submit"
            disabled={authSubmitting}
          >
            {authSubmitting ? (
              <span>Bitte warten...</span>
            ) : authMode === "login" ? (
              <>
                <span>Einloggen</span>
                <FaArrowRight />
              </>
            ) : (
              <>
                <span>Konto erstellen</span>
                <FaArrowRight />
              </>
            )}
          </button>
        </form>

        <button
          type="button"
          className="course-auth-safe"
          onClick={onToggleAuthMode}
        >
          <FaLock />
          <span>
            {authMode === "login"
              ? "Noch kein Konto? Jetzt registrieren"
              : "Schon ein Konto? Einloggen"}
          </span>
        </button>
      </article> 
    </aside>
  );
};

export default CourseAuthSection;